export type ErrorKind = 'onerror' | 'unhandledrejection' | 'manual'

export type CapturedError = {
  message: string
  name: string
  stack?: string
  source?: string
  lineno?: number
  colno?: number
  kind: ErrorKind
  handled: boolean
  fingerprint: string
}

type Reporter = (err: CapturedError) => void

// Keep payloads bounded so a runaway stack can't blow the batch size limit.
const MAX_MESSAGE = 1000
const MAX_STACK = 4000

function clamp(s: string, max: number): string {
  if (!s) return ''
  return s.length > max ? s.slice(0, max) : s
}

// Cheap, stable hash so the same error groups together server-side later.
function hash(input: string): string {
  let h = 5381
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

// Fingerprint from the error identity, not its message values (which often
// embed ids/urls). First two stack frames keep distinct call sites apart.
function fingerprintOf(name: string, message: string, stack?: string): string {
  const frames = (stack || '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('at ') || l.includes('@'))
    .slice(0, 2)
    .join('|')
  // Strip digits from the message so "user 123 not found" / "user 456 ..." collapse.
  const normMessage = message.replace(/\d+/g, '#').slice(0, 120)
  return hash(`${name}|${normMessage}|${frames}`)
}

function normalize(input: {
  error?: unknown
  message?: string
  source?: string
  lineno?: number
  colno?: number
  kind: ErrorKind
  handled: boolean
}): CapturedError {
  const err = input.error
  let name = 'Error'
  let message = input.message || ''
  let stack: string | undefined

  if (err instanceof Error) {
    name = err.name || 'Error'
    message = err.message || message
    stack = err.stack
  } else if (err && typeof err === 'object') {
    const anyErr = err as Record<string, unknown>
    name = typeof anyErr.name === 'string' ? anyErr.name : name
    message = typeof anyErr.message === 'string' ? anyErr.message : message || safeStringify(err)
    stack = typeof anyErr.stack === 'string' ? anyErr.stack : undefined
  } else if (err !== undefined && err !== null) {
    message = message || String(err)
  }

  if (!message) message = 'Unknown error'
  message = clamp(message, MAX_MESSAGE)
  stack = stack ? clamp(stack, MAX_STACK) : undefined

  return {
    message,
    name,
    stack,
    source: input.source,
    lineno: input.lineno,
    colno: input.colno,
    kind: input.kind,
    handled: input.handled,
    fingerprint: fingerprintOf(name, message, stack),
  }
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

export function startErrorTracking(report: Reporter): () => void {
  if (typeof window === 'undefined') return () => {}

  // Dedupe storm protection: same fingerprint at most once per window,
  // plus a hard cap on total errors reported per page load.
  const DEDUPE_WINDOW_MS = 5000
  const MAX_PER_PAGE = 50
  const lastSeen = new Map<string, number>()
  let total = 0

  const emit = (e: CapturedError) => {
    if (total >= MAX_PER_PAGE) return
    const now = Date.now()
    const prev = lastSeen.get(e.fingerprint)
    if (prev && now - prev < DEDUPE_WINDOW_MS) return
    lastSeen.set(e.fingerprint, now)
    total++
    report(e)
  }

  const onError = (event: ErrorEvent) => {
    emit(
      normalize({
        error: event.error,
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        kind: 'onerror',
        handled: false,
      })
    )
  }

  const onRejection = (event: PromiseRejectionEvent) => {
    emit(
      normalize({
        error: event.reason,
        kind: 'unhandledrejection',
        handled: false,
      })
    )
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}

// Exposed via Analytics.captureException for manual reporting.
export function captureToPayload(error: unknown, extra?: Record<string, unknown>): CapturedError & { extra?: Record<string, unknown> } {
  const base = normalize({ error, kind: 'manual', handled: true })
  return extra ? { ...base, extra } : base
}
